import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Badge, Button, Modal, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaArrowLeft, FaMapMarkerAlt, FaUsers, FaCheckCircle,
  FaPhone, FaEnvelope, FaUtensils, FaCamera,
  FaMusic, FaLightbulb, FaChair, FaHome, FaParking,
  FaBuilding, FaGlassCheers, FaShieldAlt, FaEnvelopeOpenText
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import '../../../assets/css/customer/packages/PackagesDetail.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY).catch((err) => {
  console.error('Stripe.js load nahi hua:', err);
  return null;
});

// Event type options — keep in sync with the venue booking screen's list
const PKGD_EVENT_TYPES = ["Wedding", "Mehndi", "Barat", "Walima", "Engagement", "Other"];

// Same services list as admin ManagePackages.js — used to turn a package's saved
// service ids back into icon + label for display
const PKGD_SERVICES = [
  { id: 'catering',      label: 'Catering',            icon: <FaUtensils /> },
  { id: 'decoration',    label: 'Decoration',           icon: <FaCamera /> },
  { id: 'photography',   label: 'Photography',          icon: <FaCamera /> },
  { id: 'videography',   label: 'Videography',          icon: <FaCamera /> },
  { id: 'music_dj',      label: 'Music & DJ',           icon: <FaMusic /> },
  { id: 'lighting',      label: 'Lighting Setup',       icon: <FaLightbulb /> },
  { id: 'valet',         label: 'Valet Parking',        icon: <FaParking /> },
  { id: 'floral',        label: 'Floral Arrangements',  icon: <FaHome /> },
  { id: 'wedding_cake',  label: 'Wedding Cake',         icon: <FaGlassCheers /> },
  { id: 'bridal_makeup', label: 'Bridal Makeup',        icon: <FaChair /> },
  { id: 'invitation',    label: 'Invitation Cards',     icon: <FaEnvelopeOpenText /> },
  { id: 'security',      label: 'Security Staff',       icon: <FaShieldAlt /> },
];

// Parse a comma-separated or JSON-stringified list coming from the backend
const parseList = (val) => {
  if (!val || val === 'null') return [];
  try {
    const parsed = JSON.parse(val);
    if (!parsed) return [];
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return String(val).split(',').map(s => s.trim()).filter(Boolean);
  }
};

// Pull the largest number out of a capacity value like "200-300" or "300" → 300
const parseMaxCapacity = (capacityVal) => {
  if (!capacityVal) return 0;
  const numbers = String(capacityVal).match(/\d+/g);
  if (!numbers || numbers.length === 0) return 0;
  return Math.max(...numbers.map(Number));
};

const formatPKR = (val) => {
  if (val === null || val === undefined || val === '' || val === 'null') return null;
  const num = Number(String(val).replace(/,/g, ''));
  if (isNaN(num)) return null;
  return "PKR " + num.toLocaleString("en-PK");
};

// ── Advance Payment Form (same as venue BookingDetails.js) ──
const PkgPaymentForm = ({ bookingId, advanceAmount, baseUrl, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [payLoading, setPayLoading] = useState(false);
  const [cardError, setCardError] = useState("");

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPayLoading(true);
    setCardError("");

    try {
      const intentRes = await axios.post(`${baseUrl}/customer/payments/create-intent`, {
        booking_id: bookingId,
      });

      if (!intentRes.data.success) {
        setCardError(intentRes.data.message || "Could not create the payment intent. Please try again.");
        setPayLoading(false);
        return;
      }

      const clientSecret = intentRes.data.clientSecret;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setCardError(result.error.message);
        setPayLoading(false);
        return;
      }

      if (result.paymentIntent.status === "succeeded") {
        await axios.post(`${baseUrl}/customer/payments/confirm`, { booking_id: bookingId });
        onPaymentSuccess();
      }
    } catch (err) {
      console.log(err);
      setCardError("Something went wrong. Please try again.");
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div>
      <p style={{ marginBottom: "10px", fontWeight: "600" }}>
        Advance Payment: PKR {Number(advanceAmount).toLocaleString("en-PK")}
      </p>
      <div style={{ border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", marginBottom: "12px" }}>
        <CardElement options={{ style: { base: { fontSize: "16px" } }, hidePostalCode: true }} />
      </div>
      {cardError && <p style={{ color: "red", fontSize: "13px" }}>{cardError}</p>}
      <Button className="pkgd-book-button" disabled={!stripe || payLoading} onClick={handlePay}>
        {payLoading ? "Processing..." : `Pay PKR ${Number(advanceAmount).toLocaleString("en-PK")} Advance`}
      </Button>
      <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
        Test card: 4242 4242 4242 4242, any future date, any CVC
      </p>
    </div>
  );
};

// ── Availability Calendar (same behaviour as the venue booking screen) ──
const PkgAvailabilityCalendar = ({ bookedDates, selectedDate, onDateSelect }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const monthNames = ["January","February","March","April","May","June",
    "July","August","September","October","November","December"];
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const formatDateStr = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const [hoveredDate, setHoveredDate] = useState(null);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const cellStyle = (state, isHovered) => {
    const base = {
      textAlign: 'center', padding: '8px 2px', borderRadius: '6px',
      fontSize: '13px', transition: 'all 0.2s',
    };
    if (state === 'empty') return { ...base, background: 'transparent', cursor: 'default' };
    if (state === 'available') {
      return {
        ...base,
        background: isHovered ? '#a7f3d0' : '#d1fae5',
        color: '#065f46',
        cursor: 'pointer',
      };
    }
    if (state === 'booked') return { ...base, background: '#fee2e2', color: '#991b1b', cursor: 'not-allowed' };
    if (state === 'selected') return { ...base, background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer' };
    if (state === 'past') return { ...base, background: '#f3f4f6', color: '#d1d5db', cursor: 'not-allowed' };
    return base;
  };

  return (
    <div style={{ width: '100%', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <button type="button" onClick={prevMonth} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '18px', color: '#7c3aed' }}>&#8249;</button>
        <span style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{monthNames[viewMonth]} {viewYear}</span>
        <button type="button" onClick={nextMonth} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '18px', color: '#7c3aed' }}>&#8250;</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
        {dayNames.map(d => (
          <span key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#9ca3af', padding: '4px 0' }}>{d}</span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} style={cellStyle('empty')} />;

          const dateStr = formatDateStr(viewYear, viewMonth, day);
          const isBooked = bookedDates.includes(dateStr);
          const isSelected = selectedDate === dateStr;
          const isPast = new Date(dateStr) < new Date(today.toDateString());

          let state = 'available';
          if (isPast) state = 'past';
          else if (isBooked) state = 'booked';
          else if (isSelected) state = 'selected';

          return (
            <span
              key={dateStr}
              style={cellStyle(state, hoveredDate === dateStr)}
              onClick={() => !isBooked && !isPast && onDateSelect(dateStr)}
              onMouseEnter={() => setHoveredDate(dateStr)}
              onMouseLeave={() => setHoveredDate(null)}
              title={isBooked ? "Already booked" : isPast ? "Past date" : "Available"}
            >
              {day}
            </span>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280' }}>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '4px', background: '#d1fae5', border: '1px solid #065f46' }} /> Available</span>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '4px', background: '#fee2e2', border: '1px solid #991b1b' }} /> Booked</span>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '4px', background: '#7c3aed' }} /> Selected</span>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '4px', background: '#f3f4f6', border: '1px solid #d1d5db' }} /> Past</span>
      </div>
    </div>
  );
};

const PackagesDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl } = useContext(AppContext);
  const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

  // ✅ The real package selected from the pricing guide (WeddingPricingGuide.js)
  const pkg = location.state?.package;

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [eventDate, setEventDate] = useState("");
  const [eventType, setEventType] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [bookedDates, setBookedDates] = useState([]);

  // ── Payment step (same flow as venue BookingDetails.js) ──
  const [showPayment, setShowPayment] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);

  if (!pkg) {
    return (
      <Container fluid="lg" style={{ padding: "60px 0", textAlign: "center" }}>
        <p>No package selected. Please go back and choose a package.</p>
        <Button variant="outline-secondary" onClick={() => navigate('/packages')}>
          <FaArrowLeft className="me-2" /> Back to Packages
        </Button>
      </Container>
    );
  }

  // ── Derive everything from the real package — no hardcoded demo data ──
  const packageName = pkg.name || 'Wedding Package';
  const venueName = pkg.venueName || pkg.venue || '—';
  const capacityDisplay = pkg.capacity ? `${pkg.capacity} guests` : '—';
  const venueCapacityMax = parseMaxCapacity(pkg.capacity);

  const originalPriceDisplay = formatPKR(pkg.price);
  const finalPriceDisplay = formatPKR(pkg.packagePrice) || originalPriceDisplay;
  const hasDiscount = pkg.discount && pkg.discount !== 'null' && originalPriceDisplay && finalPriceDisplay !== originalPriceDisplay;

  const featuresList = parseList(pkg.features);
  const serviceIds = parseList(pkg.services);
  const includedServices = serviceIds
    .map(id => PKGD_SERVICES.find(s => s.id === id))
    .filter(Boolean);

  // ✅ Package now stores a real numeric venue_id (ManagePackages.js updated to save this)
  const wevenueId = pkg.venue_id || null;

  // ── Guest count vs package capacity validation ──
  const guestCountNum = Number(guestCount || 0);
  const guestCountEntered = guestCount !== "" && guestCountNum > 0;
  const isGuestCountValid = guestCountEntered && (venueCapacityMax === 0 || guestCountNum <= venueCapacityMax);

  const canSubmitBooking = eventDate && eventType && guestCountEntered && isGuestCountValid;

  // ✅ NEW: Book This Package se pehle login check — same localStorage key
  // ('loggedInCustomer') jo VenueDetailPage.js / MyBookings.js mein use ho raha hai.
  const handleBookPackage = () => {
    if (loggedInCustomer) {
      setShowBookingModal(true);
    } else {
      navigate("/customer/login", { state: { redirectTo: "/packagesdetail", package: pkg } });
    }
  };

  // ── Fetch already-booked dates for this package's venue ──
  const fetchBookedDates = () => {
    if (!wevenueId) return;
    const formData = new FormData();
    formData.append('wevenue_id', wevenueId);
    axios.post(`${baseUrl}/customer/bookings/getBookedDates`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => { if (res.data.success) setBookedDates(res.data.bookedDates); })
    .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (showBookingModal) fetchBookedDates();
  }, [showBookingModal]);

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    if (!eventDate || !eventType || !guestCountEntered) {
      Swal.fire({ icon: 'error', title: 'Missing Information', text: 'Please select a date, event type and guest count' });
      return;
    }
    if (!isGuestCountValid) {
      Swal.fire({ icon: 'error', title: 'Too Many Guests', text: `This package's capacity is ${venueCapacityMax} guests.` });
      return;
    }
    setSubmitLoading(true);

    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);
    if (wevenueId) formData.append('wevenue_id', wevenueId);
    formData.append('wepackage_id', pkg.id);
    formData.append('date', eventDate);
    formData.append('eventType', eventType);
    formData.append('guestCount', guestCount);
    // NOTE: weevent_id has been removed because the My Event screen is being removed.
    // Once the supervisor confirms whether the backend will still create a Weevent record
    // or store eventType/guestCount directly as Webooking table columns, this formData
    // and CustomerBookingController@createBooking will both need to be updated accordingly.

    axios.post(`${baseUrl}/customer/bookings/createBooking`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
      if (res.data.success === true) {
        // ✅ Same as venue booking: don't finish here, open the advance-payment step
        setCreatedBookingId(res.data.booking.id);
        setTotalAmount(res.data.booking.totalAmount);
        setAdvanceAmount(res.data.booking.advanceAmount);
        setShowBookingModal(false);
        setShowPayment(true);
      } else {
        Swal.fire({ icon: 'error', title: 'Booking Failed', text: res.data.message || res.data.error || 'Please try again' });
      }
      setSubmitLoading(false);
    })
    .catch((err) => {
      Swal.fire({ icon: 'error', title: 'Booking Failed', text: 'Something went wrong, please try again' });
      setSubmitLoading(false);
    });
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    Swal.fire({
      icon: 'success',
      title: 'Payment Successful!',
      text: 'Advance payment received. Booking request submitted.',
      confirmButtonColor: '#7fd3c1',
      timer: 2500,
      showConfirmButton: false
    });
    setEventDate("");
    setEventType("");
    setGuestCount("");
    navigate("/customer/my-bookings");
  };

  return (
    <div className="pkgd-package-detail">
      <Container fluid="lg">
        <div className="pkgd-back-button-wrapper">
          <Button variant="outline-secondary" className="pkgd-back-button" onClick={() => navigate('/packages')}>
            <FaArrowLeft className="me-2" /> Back to Packages
          </Button>
        </div>

        <div className="pkgd-hero-section pkgd-hero-no-image">
          {hasDiscount && (
            <Badge bg="success" className="pkgd-badge-tagline mb-3">🎉 {pkg.discount}% OFF</Badge>
          )}
          <h1 className="pkgd-package-name">{packageName}</h1>
          <div className="pkgd-package-meta">
            <div className="pkgd-meta-item">
              <FaMapMarkerAlt className="pkgd-meta-icon" />
              <span>{venueName}</span>
            </div>
            <div className="pkgd-meta-item">
              <FaUsers className="pkgd-meta-icon" />
              <span>Capacity: {capacityDisplay}</span>
            </div>
          </div>
        </div>

        <Row className="pkgd-content-section" style={{ alignItems: 'flex-start' }}>
          <Col lg={8}>
            {pkg.description && (
              <Card className="pkgd-content-card">
                <Card.Body>
                  <h4 className="pkgd-section-title">About This Package</h4>
                  <p className="pkgd-description">{pkg.description}</p>
                </Card.Body>
              </Card>
            )}

            <Card className="pkgd-content-card pkgd-venue-amenities-card">
              <Card.Body>
                <h4 className="pkgd-section-title">Venue</h4>
                <div className="pkgd-venue-header">
                  <div className="pkgd-venue-icon-wrap"><FaBuilding /></div>
                  <div>
                    <h6 className="pkgd-venue-name">{venueName}</h6>
                    <p className="pkgd-venue-loc"><FaUsers className="me-1" />Capacity: {capacityDisplay}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {includedServices.length > 0 && (
              <Card className="pkgd-content-card">
                <Card.Body>
                  <h4 className="pkgd-section-title">Included Services</h4>
                  <Row className="pkgd-services-grid">
                    {includedServices.map((service) => (
                      <Col md={6} key={service.id} className="pkgd-service-col">
                        <div className="pkgd-service-item">
                          <div className="pkgd-service-icon-wrapper">{service.icon}</div>
                          <div className="pkgd-service-content">
                            <h6 className="pkgd-service-title">{service.label}</h6>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}

            {featuresList.length > 0 && (
              <Card className="pkgd-content-card">
                <Card.Body>
                  <h4 className="pkgd-section-title">Package Features</h4>
                  <Row>
                    {featuresList.map((feature, index) => (
                      <Col md={6} key={index} className="pkgd-feature-col">
                        <div className="pkgd-feature-item">
                          <FaCheckCircle className="pkgd-check-icon" />
                          <span>{feature}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col lg={4}>
            <div className="pkgd-sidebar-sticky">
              <Card className="pkgd-price-card pkgd-sidebar-card">
                <Card.Body>
                  <div className="pkgd-price-header">
                    <h6 className="pkgd-price-label">Price</h6>
                    {hasDiscount && (
                      <p style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                        {originalPriceDisplay}
                      </p>
                    )}
                    <h3 className="pkgd-price-value">{finalPriceDisplay || 'Price on request'}</h3>
                    {hasDiscount && (
                      <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
                        {pkg.discount}% off
                      </span>
                    )}
                  </div>
                  <hr className="pkgd-divider" />
                  <div className="pkgd-contact-info">
                    <div className="pkgd-contact-item"><FaPhone className="pkgd-contact-icon" /><span>03441812760</span></div>
                    <div className="pkgd-contact-item"><FaEnvelope className="pkgd-contact-icon" /><span>wed eve.com</span></div>
                  </div>
                  <Button className="pkgd-book-button" onClick={handleBookPackage}>
                    Book This Package
                  </Button>
                </Card.Body>
              </Card>

              <Card className="pkgd-cta-card">
                <Card.Body>
                  <h5 className="pkgd-cta-title">Ready to Book?</h5>
                  <p className="pkgd-cta-text">Book now and make your dream wedding a reality. Our expert team is ready to help!</p>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Booking Modal — order: 1) Event Date  2) Event Type  3) Guest Count (same as venue booking screen) */}
      <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} size="lg" centered className="pkgd-booking-modal">
        <Modal.Header closeButton className="pkgd-modal-header">
          <Modal.Title className="pkgd-modal-title">Book {packageName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pkgd-modal-body">
          <Form onSubmit={handleBookingSubmit}>

            {/* 1) Event Date */}
            <Form.Group className="mb-3">
              <Form.Label className="pkgd-form-label">Event Date *</Form.Label>
              {!wevenueId && (
                <p style={{ fontSize: "12px", color: "#92400e", marginBottom: "8px" }}>
                  ℹ️ This package isn't linked to a specific venue yet, so already-booked dates can't be shown here.
                </p>
              )}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px' }}>
                <PkgAvailabilityCalendar
                  bookedDates={bookedDates}
                  selectedDate={eventDate}
                  onDateSelect={(date) => setEventDate(date)}
                />
              </div>
            </Form.Group>

            {/* 2) Event Type */}
            <Form.Group className="mb-3">
              <Form.Label className="pkgd-form-label">Event Type *</Form.Label>
              <Form.Select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="pkgd-form-input"
                required
              >
                <option value="">Select event type</option>
                {PKGD_EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* 3) Guest Count */}
            <Form.Group className="mb-3">
              <Form.Label className="pkgd-form-label">Guest Count *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value)}
                placeholder="How many guests are coming?"
                className="pkgd-form-input"
                required
              />
              {guestCountEntered && isGuestCountValid && (
                <p style={{ fontSize: "13px", color: "#10b981", fontWeight: 600, marginTop: "6px" }}>
                  ✅ {venueName} is suitable for your {guestCountNum} guests (Capacity: {capacityDisplay}).
                </p>
              )}
              {guestCountEntered && !isGuestCountValid && (
                <p style={{ fontSize: "13px", color: "#dc2626", fontWeight: 600, marginTop: "6px" }}>
                  ⚠️ {venueName}'s capacity is {capacityDisplay} — not enough for this many guests.
                </p>
              )}
            </Form.Group>

            {/* Price Breakdown (same style as venue booking screen) */}
            {(() => {
              const rawPrice = pkg.packagePrice && pkg.packagePrice !== 'null' ? pkg.packagePrice : pkg.price;
              const totalNum = Number(String(rawPrice || 0).replace(/,/g, ''));
              if (!totalNum || isNaN(totalNum)) return null;

              const advanceNum = Math.round(totalNum * 0.10 * 100) / 100;
              const platformCommission = Math.round(totalNum * 0.05 * 100) / 100;
              const packageBooking = advanceNum - platformCommission;
              const remainingNum = totalNum - advanceNum;

              const fmt = (n) => "PKR " + n.toLocaleString("en-PK");

              return (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Total Package Price</span>
                    <strong>{fmt(totalNum)}</strong>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: '#7c3aed', fontWeight: 600, fontSize: '13px' }}>Advance Payable (10%)</span>
                    <span style={{ color: '#7c3aed', fontWeight: 700 }}>{fmt(advanceNum)}</span>
                  </div>

                  <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '4px' }}>
                      <span style={{ color: '#10b981' }}>└─ Platform Commission (5%)</span>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>{fmt(platformCommission)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                      <span style={{ color: '#7c3aed' }}>└─ Package Booking (5%)</span>
                      <span style={{ color: '#7c3aed', fontWeight: 600 }}>{fmt(packageBooking)}</span>
                    </div>
                  </div>

                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                    The remaining amount ({fmt(remainingNum)}) will be paid to the vendor after the event.
                  </p>

                  <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#92400e' }}>
                    ℹ️ Note: This price includes only what's listed under Included Services and Package Features. Any extra requests are not covered.
                  </div>
                </div>
              );
            })()}

            <div className="pkgd-modal-footer-buttons">
              <Button variant="secondary" className="pkgd-modal-btn-cancel" onClick={() => setShowBookingModal(false)}>Cancel</Button>
              <Button type="submit" className="pkgd-modal-btn-submit" disabled={submitLoading || !canSubmitBooking}>
                {submitLoading ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Submitting...</>
                ) : 'Submit Booking Request'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Advance Payment Modal — same flow as venue booking */}
      <Modal show={showPayment} centered className="pkgd-booking-modal">
        <Modal.Header className="pkgd-modal-header">
          <Modal.Title className="pkgd-modal-title">Pay Advance Amount</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pkgd-modal-body">
          <p style={{ marginBottom: "14px" }}>
            Total Price: {formatPKR(totalAmount)} &nbsp;|&nbsp; Advance: {formatPKR(advanceAmount)}
          </p>
          <Elements stripe={stripePromise}>
            <PkgPaymentForm
              bookingId={createdBookingId}
              advanceAmount={advanceAmount}
              baseUrl={baseUrl}
              onPaymentSuccess={handlePaymentSuccess}
            />
          </Elements>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PackagesDetail;